import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import { useFormState } from '@/hooks/useFormState';
import { useSocialLogin } from '@/hooks/useSocialLogin';
import { emailLogin } from '@/services/authService';
import { validateLoginForm } from '@/utils/authValidation';

import { AuthFormLayout } from '../common/AuthFormLayout';
import { Button } from '../common/Button';
import { FormInput } from '../common/FormInput';
import { TermsCheckbox } from '../common/TermsCheckbox';

interface LoginFormData {
  email: string;
  password: string;
  terms: boolean;
}

// 获取query ticket并传递
const urlParams = new URLSearchParams(window.location.search);
const ticket = urlParams.get('ticket') || undefined;
const product = urlParams.get('product') || undefined;

export const LoginForm = () => {
  const { t } = useTranslation();
  const { handleGithubLogin, handleGoogleLogin } = useSocialLogin();

  const { values, errors, isLoading, setValue, setError, clearError, setLoading, reset } =
    useFormState<LoginFormData>({
      initialValues: {
        email: '',
        password: '',
        terms: true,
      },
      validate: validateLoginForm,
    });

  // Handle email login
  const handleEmailLogin = async () => {
    const validationErrors = validateLoginForm(values);

    if (Object.keys(validationErrors).length > 0) {
      Object.entries(validationErrors).forEach(([field, error]) => {
        setError(field, error);
      });

      if (validationErrors.terms) {
        toast.error(t('loginForm.termsError'));
      }
      return;
    }

    try {
      setLoading(true);

      const response = await emailLogin({
        email: values.email,
        password: values.password,
        auth_ticket_id: ticket,
        product,
      });

      if (response.status === 200) {
        window.location.reload();
      } else {
        toast.error(response.message || t('loginForm.loginError'));
      }
    } catch (error: any) {
      const errorMessage = error.message || t('loginForm.loginError');
      setError('general', errorMessage);
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  // Handle social login with loading state and form reset
  const handleSocialLogin = async (loginFn: (ticket?: string, product?: string) => void) => {
    try {
      setLoading(true);
      // 在跳转前重置表单状态，清除输入内容和校验状态
      // 这样可以避免用户从第三方登录返回后，表单内容仍然保留的问题
      reset();
      // 延迟执行跳转，确保状态重置完成
      // 100ms 的延迟足够让 React 状态更新完成
      setTimeout(() => {
        loginFn(ticket, product);
      }, 100);
    } catch (_error) {
      setLoading(false);
    }
  };

  // Handle Enter key press
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !isLoading) {
      e.preventDefault();
      handleEmailLogin();
    }
  };

  return (
    <AuthFormLayout
      onGithubLogin={() => handleSocialLogin(handleGithubLogin)}
      onGoogleLogin={() => handleSocialLogin(handleGoogleLogin)}
      isLoading={isLoading}
      generalError={errors.general}
    >
      <form
        onSubmit={(e) => {
          e.preventDefault();
          handleEmailLogin();
        }}
        onKeyDown={handleKeyDown}
      >
        {/* Email input */}
        <FormInput
          type="email"
          name="email"
          id="login-email"
          autoComplete="username"
          value={values.email}
          onChange={(e) => {
            setValue('email', e.target.value);
            clearError('email');
            clearError('general');
          }}
          placeholder={t('loginForm.emailPlaceholder')}
          error={errors.email}
          tabIndex={1}
        />

        {/* Password input */}
        <FormInput
          type="password"
          name="password"
          id="login-password"
          autoComplete="current-password"
          value={values.password}
          onChange={(e) => {
            setValue('password', e.target.value);
            clearError('password');
            clearError('general');
          }}
          placeholder={t('loginForm.passwordPlaceholder')}
          error={errors.password}
          tabIndex={2}
        />

        {/* Terms agreement and login button */}
        <div className="flex flex-col gap-[8px]">
          <TermsCheckbox
            checked={values.terms}
            onChange={(e) => {
              setValue('terms', e.target.checked);
              clearError('terms');
            }}
            error={errors.terms}
          />

          <Button
            onClick={handleEmailLogin}
            disabled={isLoading}
            isLoading={isLoading}
            tabIndex={3}
          >
            {t('loginForm.loginButton')}
          </Button>
        </div>

        {/* Bottom links */}
        <div className="mt-[20px] flex flex-col items-center gap-[4px]">
          <p className="text-[12px] font-medium text-black ">
            <span>{t('loginForm.noAccount')} </span>
            <Link
              to={`/register${window.location.search}`}
              className="auth-link text-[#252626] hover:text-[#326BFB]"
            >
              {t('loginForm.createAccount')}
            </Link>
          </p>

          <Link
            to="/forgot-password"
            className="auth-link text-[12px] font-medium text-black hover:text-[#326BFB]"
          >
            {t('loginForm.forgotPassword')}
          </Link>
        </div>
      </form>
    </AuthFormLayout>
  );
};